import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertCircle, Edit, Plus, Send, Trash2 } from 'lucide-react';
import { getBackendUrl } from '../../../utils/api';

const API = `${getBackendUrl()}/api`;

const WEEKDAYS = [
  { v: 0, label: 'Seg' }, { v: 1, label: 'Ter' }, { v: 2, label: 'Qua' },
  { v: 3, label: 'Qui' }, { v: 4, label: 'Sex' }, { v: 5, label: 'Sáb' }, { v: 6, label: 'Dom' },
];
const emptyStatusForm = { id: null, title: '', image_url: '', caption: '', days_of_week: [], time: '09:00', enabled: true };

const StatusPostsManager = ({ connected, goToConnection }) => {
  const [schedules, setSchedules] = useState([]);
  const [form, setForm] = useState(emptyStatusForm);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/whatsapp/status-schedules`);
      setSchedules(r.data.schedules || []);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const toggleDay = (v) => {
    setForm((f) => ({ ...f, days_of_week: f.days_of_week.includes(v)
      ? f.days_of_week.filter((d) => d !== v) : [...f.days_of_week, v] }));
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/products/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((f) => ({ ...f, image_url: r.data.image_url }));
      toast.success('Imagem enviada!');
    } catch (err) {
      toast.error('Falha ao enviar imagem.');
    } finally { setUploading(false); }
  };

  const openNew = () => { setForm(emptyStatusForm); setShowForm(true); };
  const openEdit = (s) => { setForm({ ...s }); setShowForm(true); };

  const save = async () => {
    if (!form.title.trim() || !form.image_url) { toast.error('Informe um título e envie uma imagem.'); return; }
    if (form.days_of_week.length === 0) { toast.error('Selecione ao menos um dia da semana.'); return; }
    try {
      setSaving(true);
      const payload = { title: form.title, image_url: form.image_url, caption: form.caption,
        days_of_week: form.days_of_week, time: form.time, enabled: form.enabled };
      if (form.id) await axios.put(`${API}/whatsapp/status-schedules/${form.id}`, payload);
      else await axios.post(`${API}/whatsapp/status-schedules`, payload);
      toast.success('Agendamento salvo!');
      setShowForm(false); setForm(emptyStatusForm); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar.');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir este agendamento?')) return;
    try { await axios.delete(`${API}/whatsapp/status-schedules/${id}`); toast.success('Excluído.'); load(); }
    catch (e) { toast.error('Erro ao excluir.'); }
  };

  const postNow = async (id) => {
    try { await axios.post(`${API}/whatsapp/status-schedules/${id}/post-now`); toast.success('Status publicado agora!'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro ao publicar.'); }
  };

  return (
    <div className="space-y-5" data-testid="status-posts-manager">
      {!connected && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-950/30 p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-amber-200 text-sm font-medium">WhatsApp não conectado</p>
            <p className="text-amber-100/80 text-xs mt-1">Você pode criar os agendamentos agora; eles só serão publicados quando o WhatsApp estiver conectado.</p>
            <button onClick={goToConnection} className="mt-2 text-xs text-amber-200 hover:text-white underline">Ir para Conexão WhatsApp →</button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Status agendados (Stories)</h3>
          <p className="text-xs text-gray-400">Publique imagens no seu Status automaticamente em dias e horários definidos.</p>
        </div>
        <button onClick={openNew} data-testid="status-add-button"
          className="bg-pink-500 hover:bg-pink-600 text-white font-semibold px-4 py-2 rounded-xl transition-colors flex items-center gap-2">
          <Plus size={18} /> Novo
        </button>
      </div>

      {showForm && (
        <div className="glass-card border-2 border-pink-500/30 space-y-4" data-testid="status-form">
          <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Título (ex: Abertura da loja)" className="glass-input w-full" data-testid="status-title-input" />

          <div>
            <label className="block text-xs text-gray-400 mb-1">Imagem do status</label>
            <div className="flex items-center gap-3">
              {form.image_url ? (
                <img src={form.image_url} alt="preview" className="w-20 h-20 object-cover rounded-lg border border-white/10" />
              ) : (
                <div className="w-20 h-20 rounded-lg bg-slate-800 border border-white/10 flex items-center justify-center text-gray-500 text-xs">sem imagem</div>
              )}
              <label className="cursor-pointer bg-slate-700 hover:bg-slate-600 text-white text-sm px-4 py-2 rounded-xl transition-colors" data-testid="status-upload-label">
                {uploading ? 'Enviando...' : 'Enviar imagem'}
                <input type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="status-upload-input" />
              </label>
            </div>
          </div>

          <textarea value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })}
            placeholder="Legenda (opcional)" rows={2} className="glass-input w-full resize-none" data-testid="status-caption-input" />

          <div>
            <label className="block text-xs text-gray-400 mb-2">Dias da semana</label>
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map((d) => (
                <button key={d.v} type="button" onClick={() => toggleDay(d.v)}
                  data-testid={`status-day-${d.v}`}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    form.days_of_week.includes(d.v) ? 'bg-pink-500 text-white' : 'bg-slate-800 text-gray-400 hover:bg-slate-700'
                  }`}>{d.label}</button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Horário</label>
              <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
                className="glass-input" data-testid="status-time-input" />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer mt-5">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="w-4 h-4 rounded" />
              Ativo
            </label>
          </div>

          <div className="flex gap-2 pt-2">
            <button onClick={save} disabled={saving} data-testid="status-save-button"
              className="bg-pink-500 hover:bg-pink-600 text-white font-semibold px-5 py-2 rounded-xl transition-colors disabled:opacity-50">
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
            <button onClick={() => { setShowForm(false); setForm(emptyStatusForm); }}
              className="bg-slate-700 hover:bg-slate-600 text-white px-5 py-2 rounded-xl transition-colors">Cancelar</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {schedules.length === 0 && !showForm && (
          <p className="text-center text-gray-500 text-sm py-6">Nenhum status agendado ainda. Clique em &quot;Novo&quot;.</p>
        )}
        {schedules.map((s) => (
          <div key={s.id} className="glass-card flex items-center gap-4" data-testid={`status-item-${s.id}`}>
            <img src={s.image_url} alt={s.title} className="w-16 h-16 object-cover rounded-lg border border-white/10 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-white truncate">{s.title}</p>
                {!s.enabled && <span className="text-[10px] bg-gray-700 text-gray-300 px-2 py-0.5 rounded-full">Pausado</span>}
              </div>
              <p className="text-xs text-gray-400 truncate">{s.caption || 'Sem legenda'}</p>
              <p className="text-xs text-pink-300 mt-1">
                ⏰ {s.time} · {s.days_of_week.map((d) => WEEKDAYS.find((w) => w.v === d)?.label).join(', ')}
              </p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={() => postNow(s.id)} title="Publicar agora" data-testid={`status-postnow-${s.id}`}
                className="p-2 text-emerald-400 hover:bg-emerald-500/10 rounded-lg transition-colors"><Send size={16} /></button>
              <button onClick={() => openEdit(s)} title="Editar" className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"><Edit size={16} /></button>
              <button onClick={() => remove(s.id)} title="Excluir" className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StatusPostsManager;
